<template>
  <div class="service-step-container">
    <!-- Username Input Section -->
    <a-card v-if="!hideUsername" class="username-card card">
      <div class="username-section">
        <div class="username-header">
          <a-icon type="user" class="username-icon" />
          <h3 class="username-title">{{ $t('PAYMENT_STEP_ENTER_USERNAME') }}</h3>
        </div>
        <div v-if="!paymentUsername && triggerUsername" class="error-message">
          <a-icon type="exclamation-circle" />
          <small>{{ $t('PLEASE_ENTER_USERNAME') }}</small>
        </div>
        <a-input
          :value="paymentUsername"
          :placeholder="$t('PLEASE_ENTER_USERNAME')"
          :disabled="disabledUsername"
          size="large"
          class="username-input"
          @change="handleUsernameChange"
        >
          <a-tooltip
            slot="suffix"
            :title="$t('PLEASE_ENTER_USERNAME_TO_PAYMENT')"
          >
            <a-icon
              type="info-circle"
              class="info-icon"
            />
          </a-tooltip>
        </a-input>
      </div>
    </a-card>

    <!-- Service Selection Section -->
    <a-card class="service-card card">
      <div slot="title" class="service-card-title">
        <a-icon type="gift" />
        <span>{{ $t('PAYMENT_STEP_SELECT_SERVICE') }}</span>
      </div>

      <a-tabs
        :activeKey="activeService"
        class="service-tabs"
        @change="handleServiceTabChange"
      >
        <a-tab-pane
          v-for="group in option.service"
          :key="group.name"
          :tab="group.label"
        >
          <a-radio-group
            :value="paymentServiceId"
            class="service-radio-group"
            @change="handleServiceChange"
          >
            <div
              :class="getGridClass(group.name)"
            >
              <div
                v-for="item in group.value"
                :key="item.id"
                :class="['service-option-card', {
                  'service-option-selected': isSelectedService(item),
                  'service-option-disabled': item.disabled,
                  'service-option-best-choice': item.bestChoice
                }]"
                class="hand-mouse"
                @click="!item.disabled && handleServiceClick(item)"
              >
                <div
                  v-if="item.bestChoice"
                  class="service-best-choice-badge"
                >
                  <a-icon type="star" />
                  <span>{{ $t('BEST_CHOICE') }}</span>
                </div>
                <div class="service-option-header">
                  <div class="service-option-logo-container">
                    <img
                      v-if="item.logo"
                      :src="item.logo"
                      class="service-option-logo"
                      :alt="item.title || item.name"
                    >
                    <div v-else class="service-option-logo-placeholder">
                      <a-icon type="appstore" />
                    </div>
                  </div>
                  <div class="service-option-title-section">
                    <a-radio
                      :value="item.id"
                      :disabled="item.disabled"
                      class="service-option-radio"
                    >
                      <span class="service-option-name">
                        {{ item.title || item.name }}
                      </span>
                    </a-radio>
                    <span
                      v-if="item.subTitle"
                      class="service-option-subtitle"
                    >
                      {{ item.subTitle }}
                    </span>
                    <span
                      v-if="item.totalMonths"
                      class="service-option-total-months"
                    >
                      {{ $t('TOTAL_MONTHS', { months: item.totalMonths }) }}
                    </span>
                    <span
                      v-if="item.hotPrice"
                      class="service-hot-badge"
                    >
                      {{ item.hotPrice }}
                    </span>
                  </div>
                </div>

                <div class="service-option-content">
                  <div
                    v-if="item.promotionStartDate && item.promotionEndDate"
                    class="service-promotion-period"
                  >
                    <a-icon type="clock-circle" class="promotion-period-icon" />
                    <span class="promotion-period-text">
                      {{ $t('PROMOTION_PERIOD', { start: item.promotionStartDate, end: item.promotionEndDate }) }}
                    </span>
                  </div>
                  <div v-if="item.description && item.description.length" class="service-features">
                    <div
                      v-for="(desc,index) in item.description"
                      :key="index"
                      class="service-feature-item"
                    >
                      <a-icon type="check-circle" class="feature-icon" />
                      <span v-html="desc" class="feature-text" />
                    </div>
                  </div>

                  <div v-if="item.promotion && item.promotion.length" class="service-promotions">
                    <div
                      v-for="(promotion,index) in item.promotion"
                      :key="index"
                      class="service-promotion-item"
                    >
                      <a-icon type="gift" class="promotion-icon" />
                      <a href="#" class="promotion-link" v-html="promotion" />
                    </div>
                  </div>
                </div>

                <div class="service-option-footer">
                  <div class="service-price-section">
                    <del v-if="item.beforePrice" class="service-price-old">
                      {{ item.beforePrice | addCommas }} {{ currency }}
                    </del>
                    <div class="service-price-current">
                      {{ item.price | addCommas }} {{ currency }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a-radio-group>
        </a-tab-pane>
      </a-tabs>
    </a-card>
  </div>
</template>

<script>
export default {
  name: 'ServiceStep',
  props: {
    paymentUsername: {
      type: String,
      default: '',
    },
    triggerUsername: {
      type: Boolean,
      default: false,
    },
    activeService: {
      type: String,
      required: true,
    },
    paymentServiceId: {
      type: [String, Number],
      default: '',
    },
    option: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    disabledUsername: {
      type: Boolean,
      default: false,
    },
    hideUsername: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isSelectedService(item) {
      return this.paymentServiceId === item.id;
    },
    getGridClass(serviceName) {
      const isFourColumns = serviceName && (
        serviceName.includes('tiktok')
        || serviceName.includes('lazada')
        || serviceName.includes('subcheo')
      );

      return [
        'service-grid',
        {
          'service-grid-4-columns': isFourColumns,
        },
      ];
    },
    handleUsernameChange(e) {
      this.$emit('username-change', e.target.value);
    },
    handleServiceTabChange(key) {
      this.$emit('service-tab-change', key);
    },
    handleServiceChange(e) {
      this.$emit('service-change', e.target.value);
    },
    handleServiceClick(item) {
      this.$emit('service-click', item);
    },
  },
};
</script>

<style scoped>
/* Service Step Container */
.service-step-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

/* Username Card */
.username-card {
  margin-bottom: 0;
}

.username-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.username-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
}

.username-icon {
  color: var(--primary-color);
  font-size: 20px;
}

.username-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.error-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-xs);
  margin-bottom: var(--spacing-xs);
  color: var(--error-color);
  font-size: var(--font-size-sm);
}

.error-message .anticon {
  color: var(--error-color);
}

.username-input {
  margin-top: var(--spacing-sm);
  border-radius: var(--border-radius-md);
}

.info-icon {
  color: var(--text-tertiary);
}

/* Service Card */
.service-card {
  margin-top: 0;
}

.service-card-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.service-card-title .anticon {
  color: var(--primary-color);
  font-size: 18px;
}

.service-tabs {
  margin-top: var(--spacing-md);
}

/* Service Grid Layout */
.service-radio-group {
  width: 100%;
}

.service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
}

.service-grid.service-grid-4-columns {
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  min-width: 0;
}

@media (min-width: 1201px) {
  .service-grid.service-grid-4-columns {
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  }
}

@media (max-width: 1200px) {
  .service-grid.service-grid-4-columns {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 768px) {
  .service-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-sm);
  }

  .service-grid.service-grid-4-columns {
    grid-template-columns: 1fr;
  }
}

/* Service Option Card */
.service-option-card {
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg) var(--spacing-md) var(--spacing-md);
  background: var(--bg-primary);
  transition: border-color var(--transition-base), background-color var(--transition-base), box-shadow var(--transition-base);
  cursor: pointer;
  touch-action: manipulation;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  position: relative;
}

.service-option-card:hover {
  border-color: var(--primary-color);
}

.service-option-card.service-option-selected {
  border-color: var(--primary-color);
  background: var(--primary-subtle);
  box-shadow: 0 0 0 1px var(--primary-color);
}

.service-option-card.service-option-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.service-option-card.service-option-best-choice {
  border-color: var(--primary-color);
  background: var(--primary-light);
}

.service-option-card.service-option-best-choice.service-option-selected {
  background: var(--primary-light);
}

.service-option-best-choice .service-option-total-months,
.service-option-best-choice .service-price-old {
  color: var(--text-secondary);
}

.service-best-choice-badge {
  position: absolute;
  top: -10px;
  left: 12px;
  background: var(--primary-color);
  color: var(--text-inverse);
  padding: 3px 8px;
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 10;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.service-best-choice-badge .anticon {
  font-size: 12px;
}

.service-option-header {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-color);
}

.service-option-logo-container {
  flex-shrink: 0;
}

.service-option-logo {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: var(--border-radius-md);
  background: var(--bg-secondary);
  padding: var(--spacing-xs);
}

.service-option-logo-placeholder {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border-radius: var(--border-radius-md);
  color: var(--text-tertiary);
  font-size: 24px;
}

.service-option-title-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.service-option-radio {
  width: 100%;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
}

.service-option-name {
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
}

.service-option-selected .service-option-name {
  color: var(--primary-color);
}

.service-option-subtitle {
  color: var(--primary-color);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  margin-top: var(--spacing-xs);
  text-align: left;
  align-self: flex-start;
  display: block;
  width: 100%;
}

.service-option-selected .service-option-subtitle {
  color: var(--primary-color);
}

.service-option-total-months {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  margin-top: var(--spacing-xs);
  text-align: left;
  align-self: flex-start;
  display: block;
  width: 100%;
}

.service-option-selected .service-option-total-months {
  color: var(--primary-color);
}

.service-hot-badge {
  display: inline-block;
  padding: 1px 8px;
  background: var(--discount-bg);
  color: var(--discount-text);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--border-radius-full);
  margin-top: var(--spacing-xs);
  align-self: flex-start;
}

.service-option-content {
  flex: 1;
  margin-bottom: var(--spacing-md);
}

.service-promotion-period {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--primary-light);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-sm);
}

.promotion-period-icon {
  color: var(--primary-color);
  font-size: 14px;
}

.promotion-period-text {
  color: var(--primary-color);
  font-weight: var(--font-weight-medium);
}

.service-option-selected .service-promotion-period,
.service-option-best-choice .service-promotion-period {
  background: var(--bg-primary);
}

.service-features {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-sm);
}

.service-feature-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
  color: var(--text-secondary);
}

.feature-icon {
  color: var(--success-color);
  font-size: 14px;
  margin-top: 3px;
  flex-shrink: 0;
}

.feature-text {
  flex: 1;
}

.service-promotions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--border-color);
}

.service-promotion-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
}

.promotion-icon {
  color: var(--primary-color);
  font-size: 14px;
  flex-shrink: 0;
}

.promotion-link {
  color: var(--primary-color);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.promotion-link:hover {
  color: var(--primary-hover);
  text-decoration: underline;
}

.promotion-link::v-deep .promo-follow-highlight {
  display: inline-block;
  background: var(--primary-subtle);
  color: var(--primary-hover);
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
  border: 1px dashed var(--primary-color);
  font-weight: var(--font-weight-semibold);
  animation: promo-follow-pulse 2s ease-in-out infinite;
}

.promotion-link::v-deep .promo-follow-highlight strong {
  color: var(--primary-color);
  font-weight: 800;
  letter-spacing: 0.3px;
}

@keyframes promo-follow-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.35);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0);
  }
}

.service-option-footer {
  margin-top: auto;
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--border-color);
}

.service-price-section {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.service-price-old {
  color: var(--text-tertiary);
  font-size: var(--font-size-sm);
  text-decoration: line-through;
}

.service-price-current {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.01em;
  color: var(--text-primary);
}

.service-option-selected .service-price-current {
  color: var(--primary-hover);
}

@media (max-width: 768px) {
  .service-option-card {
    min-height: auto;
    padding: var(--spacing-sm);
  }

  .service-option-logo,
  .service-option-logo-placeholder {
    width: 48px;
    height: 48px;
  }

  .service-option-name {
    font-size: var(--font-size-sm);
  }

  .service-price-current {
    font-size: var(--font-size-lg);
  }

  .service-features,
  .service-promotions {
    font-size: var(--font-size-xs);
  }
}

.hand-mouse {
  cursor: pointer;
}
</style>
